import { Avatar, Box, Button, Chip, Divider, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { Provider } from "../../types/connecteeSubmission";
import {
  getProviderDisplayCredentials,
  getProviderDisplayName,
  getProviderFirstName,
} from "../../utils/providerDisplay";

const MAGENTA = "#A63D7E";
const TEAL = "#008080";

interface ProviderCardProps {
  provider: Provider;
  layout?: "horizontal" | "stacked";
}

export function ProviderCard({ provider, layout = "horizontal" }: ProviderCardProps) {
  const name = getProviderDisplayName(provider);
  const firstName = getProviderFirstName(provider);
  const credentials = getProviderDisplayCredentials(
    provider.professionalCredentials,
    provider.otherProfessionalCredentials
  );
  const specialties = (provider.providerProfileInfo?.clinicalSpecialties ?? []).slice(0, 3);
  const isStacked = layout === "stacked";

  const actionButtons = (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        flexShrink: 0,
        alignSelf: isStacked ? "stretch" : { xs: "stretch", md: "center" },
        width: isStacked ? "100%" : "auto",
      }}
    >
      <Button
        variant="contained"
        startIcon={<ChatBubbleOutlineIcon />}
        disableElevation
        component="a"
        href={provider.email ? `mailto:${provider.email}` : undefined}
        disabled={!provider.email}
        sx={{
          bgcolor: TEAL,
          color: "#fff",
          textTransform: "none",
          fontWeight: 600,
          px: 2.5,
          py: 1.25,
          whiteSpace: "nowrap",
          flexDirection: "row",
          gap: 1,
          flex: isStacked ? 1 : undefined,
          "&:hover": { bgcolor: "#006666" },
          "& .MuiSvgIcon-root": { fontSize: 18 },
        }}
      >
        Message {firstName}
      </Button>
      {provider.uuid && (
        <Button
          variant="outlined"
          component={RouterLink}
          to={`/providers/${provider.uuid}`}
          sx={{
            borderColor: MAGENTA,
            color: MAGENTA,
            textTransform: "none",
            fontWeight: 600,
            px: 2.5,
            py: 1.25,
            whiteSpace: "nowrap",
            flexDirection: "row",
            flex: isStacked ? 1 : undefined,
            "&:hover": { borderColor: MAGENTA, bgcolor: "rgba(166, 61, 126, 0.04)" },
          }}
        >
          View Full Profile
        </Button>
      )}
    </Stack>
  );

  const contactInfo = (
    <Stack spacing={0.5}>
      {provider.phone && (
        <Stack direction="row" spacing={1} alignItems="center">
          <PhoneOutlinedIcon sx={{ fontSize: 18, color: "#757575" }} />
          <Typography variant="body2" color="#616161">
            {provider.phone}
          </Typography>
        </Stack>
      )}
      {provider.email && (
        <Stack direction="row" spacing={1} alignItems="center">
          <MailOutlineIcon sx={{ fontSize: 18, color: "#757575" }} />
          <Typography variant="body2" color="#616161" noWrap>
            {provider.email}
          </Typography>
        </Stack>
      )}
    </Stack>
  );

  const specialtiesRow =
    specialties.length > 0 ? (
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {specialties.map((specialty) => (
          <Chip
            key={specialty}
            label={specialty}
            size="small"
            sx={{ bgcolor: "#F0F0F0", color: "#424242", borderRadius: 1 }}
          />
        ))}
      </Stack>
    ) : null;

  if (isStacked) {
    return (
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #E0E0E0",
          borderRadius: 2,
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          height: "100%",
          boxSizing: "border-box",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Avatar
            src={provider.avatar || provider.avatarThumb || undefined}
            alt={name}
            variant="rounded"
            sx={{
              width: 180,
              height: 180,
              bgcolor: "#CFD8DC",
              flexShrink: 0,
              borderRadius: 2,
              "& img": { objectFit: "cover" },
            }}
          />
          <Stack spacing={1.25} flex={1} minWidth={0}>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#212121">
                {name}
              </Typography>
              {credentials && (
                <Typography
                  variant="body2"
                  sx={{
                    color: TEAL,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontWeight: 600,
                    mt: 0.25,
                  }}
                >
                  {credentials}
                </Typography>
              )}
            </Box>
            {specialtiesRow}
            {contactInfo}
          </Stack>
        </Stack>
        <Divider />

        {actionButtons}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E0E0E0",
        borderRadius: 2,
        p: 3,
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 3,
        alignItems: { xs: "stretch", md: "center" },
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "center", sm: "flex-start" }}
        flex={1}
        minWidth={0}
      >
        <Avatar
          src={provider.avatar || provider.avatarThumb || undefined}
          alt={name}
          sx={{
            width: 128,
            height: 128,
            bgcolor: "#CFD8DC",
            flexShrink: 0,
            "& img": { objectFit: "cover" },
          }}
        />

        <Stack spacing={1.25} flex={1} minWidth={0}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#212121">
              {name}
            </Typography>
            {credentials && (
              <Typography variant="body2" sx={{ color: TEAL, mt: 0.25 }}>
                {credentials}
              </Typography>
            )}
          </Box>
          {specialtiesRow}
          {contactInfo}
        </Stack>
      </Stack>
      <Divider />

      {actionButtons}
    </Box>
  );
}
